import { Box, styled } from '@mui/material'

import type { PageLayoutProps } from './PageLayout.types'

const FALLBACK_NAV_WIDTH = '240px'
const FALLBACK_SIDEBAR_WIDTH = '400px'
const FALLBACK_SIDEBAR_COLLAPSED_WIDTH = '30px'

type PageLayoutRootProps = Pick<
  PageLayoutProps,
  'variant' | 'sidebarOpen' | 'sidebarWidth' | 'sidebarCollapsedWidth' | 'navHidden' | 'contentBottomInset'
> & {
  shouldTransition?: boolean
  shouldRenderGhost?: boolean
}

export const toCssLength = (value?: number | string) => (typeof value === 'number' ? `${value}px` : value)

export const PageLayoutRoot = styled(Box, {
  name: 'PageLayout',
  slot: 'Root',
  overridesResolver: (_props, styles) => styles.root,
  shouldForwardProp: (prop) =>
    prop !== 'variant' &&
    prop !== 'sidebarOpen' &&
    prop !== 'sidebarWidth' &&
    prop !== 'sidebarCollapsedWidth' &&
    prop !== 'navHidden' &&
    prop !== 'contentBottomInset' &&
    prop !== 'shouldTransition' &&
    prop !== 'shouldRenderGhost',
})<PageLayoutRootProps>(({
  theme,
  variant = 'one-column',
  sidebarOpen,
  sidebarWidth,
  sidebarCollapsedWidth,
  navHidden,
  contentBottomInset,
  shouldTransition = true,
  shouldRenderGhost,
}) => {
  const navWidth = theme.mixins?.sidebar?.width ? theme.mixins?.sidebar?.width.toString() : FALLBACK_NAV_WIDTH
  const openWidth =
    toCssLength(sidebarWidth) ?? toCssLength(theme.mixins?.rightSidebar?.width) ?? FALLBACK_SIDEBAR_WIDTH
  const collapsedWidth =
    toCssLength(sidebarCollapsedWidth) ??
    toCssLength(theme.mixins?.rightSidebar?.collapsedWidth) ??
    FALLBACK_SIDEBAR_COLLAPSED_WIDTH

  const isTwoColumnVariant = variant === 'two-column' || variant === 'two-column-with-sidebar'

  let width = '100%'

  if (variant === 'two-column') {
    width = navHidden ? '100%' : `calc(100% - ${navWidth})`
  } else if (variant === 'two-column-with-sidebar') {
    width = navHidden
      ? `calc(100% - ${sidebarOpen ? openWidth : collapsedWidth})`
      : `calc(100% - ${navWidth} - ${sidebarOpen ? openWidth : collapsedWidth})`
  }

  return {
    marginTop: shouldRenderGhost ? 0 : theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    marginLeft: navHidden && isTwoColumnVariant ? 0 : isTwoColumnVariant ? navWidth : 'auto',
    flex: 1,
    width,
    maxHeight: contentBottomInset ? `calc(100vh - ${toCssLength(contentBottomInset)})` : '100vh',
    overflowY: 'scroll',
    ...(shouldTransition
      ? {
          transition: theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }
      : {}),
  }
})
